import {
  ComponentPropsWithoutRef,
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { FloatingFocusManager } from "@floating-ui/react";
import styles from "./Popover.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../../hooks";
import { usePopoverContext } from "./internal/usePopoverContext";

/**
 * Props for the PopoverContent component.
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PopoverContentProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "popover"
> & {
  /** Reference to the scrollable content area. */
  scrollerRef?: RefObject<HTMLDivElement>;
  /** Whether to unmount children from the DOM while the popover is closed. Useful for performance when the content is expensive to render. */
  hideWhileClosed?: boolean;
};

/**
 * PopoverContent component for the main popover content area.
 *
 * Features:
 * - HTML Popover API top-layer rendering (popover="manual")
 * - Floating UI positioning via refs.setFloating and floatingStyles
 * - FloatingFocusManager for focus trapping (modal) or Tab-out closing (non-modal)
 * - Arrow/caret indicator with position computed from middlewareData.arrow
 * - Scroll position reset when the popover opens
 * - Optional scrollerRef for external scroll access
 *
 * @example
 * <Popover.Content>
 *   <p>Popover content</p>
 *   <Popover.Close>Close</Popover.Close>
 * </Popover.Content>
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent(props, ref) {
    const {
      children,
      className,
      style,
      onScroll,
      onScrollCapture,
      scrollerRef,
      hideWhileClosed = false,
      ...rest
    } = props;

    const {
      floatingContext,
      refs,
      floatingStyles,
      middlewareData,
      getFloatingProps,
      openState,
      actualPlacement,
      arrowRef,
      modal,
      openOnHover,
      noPadding,
      enableCaret,
      triggerId,
      role,
    } = usePopoverContext();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const mergedFloatingRef = useMergeRefs([refs.setFloating, ref]);
    const mergedScrollerRef = useMergeRefs([scrollContainerRef, scrollerRef]);

    // FloatingFocusManager's built-in returnFocus never fires since the element
    // stays mounted (popover="manual"). Manually return focus to the trigger on
    // close; skip on initial render and for hover popovers (would re-trigger useFocus).
    // Applies to all roles — non-dialog keyboard navigation (e.g. Listbox arrow keys)
    // can move DOM focus into the content, so we must always return focus on close.
    const hasBeenOpenRef = useRef(false);
    useEffect(() => {
      if (openState) {
        hasBeenOpenRef.current = true;
      } else if (hasBeenOpenRef.current && !openOnHover) {
        // Only return focus to the reference if focus hasn't already moved to another
        // interactive element (e.g. Tab-out on combobox popovers like DateField).
        // If focus is still inside the floating content or on document.body (no meaningful
        // target), return focus to the reference as normal.
        const activeEl = document.activeElement;
        const floating = floatingContext.elements.floating;
        // For dialog role: return focus if stranded (null, body, or inside floating).
        // For non-dialog (combobox) roles: only return focus if it's still inside the
        // floating — hidePopover() already restores focus on close, so the body check
        // is not needed and would incorrectly return focus on Tab-out.
        const shouldReturnFocus =
          role === "dialog"
            ? !activeEl ||
              activeEl === document.body ||
              floating?.contains(activeEl)
            : floating?.contains(activeEl);
        if (shouldReturnFocus) {
          (floatingContext.elements.reference as HTMLElement | null)?.focus();
        }
      }
    }, [
      openState,
      openOnHover,
      floatingContext.elements.reference,
      floatingContext.elements.floating,
      role,
    ]);

    // Reset scroll position when popover opens.
    useLayoutEffect(() => {
      if (openState && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
        scrollContainerRef.current.scrollLeft = 0;
      }
    }, [openState]);

    // Arrow x/y offset along the floating edge; static side is opposite the placement.
    const arrowX = middlewareData.arrow?.x ?? null;
    const arrowY = middlewareData.arrow?.y ?? null;
    const baseSide = actualPlacement.split("-")[0] as
      | "top"
      | "bottom"
      | "left"
      | "right";
    const staticSide = (
      { top: "bottom", right: "left", bottom: "top", left: "right" } as const
    )[baseSide];

    // SVG path is a downward triangle by default; rotate to match placement direction.
    const arrowDeg =
      staticSide === "top"
        ? 180
        : staticSide === "bottom"
          ? 0
          : staticSide === "left"
            ? 90
            : 270;

    // Pull the arrow past the content edge by its width minus 2px border overlap.
    const arrowWidth = arrowRef.current?.offsetWidth ?? 14;
    const arrowStyle: CSSProperties = {};
    if (arrowX != null) arrowStyle.left = `${arrowX}px`;
    if (arrowY != null) arrowStyle.top = `${arrowY}px`;
    (arrowStyle as Record<string, string>)[staticSide] = `${-arrowWidth + 2}px`;
    arrowStyle.transform = `rotate(${arrowDeg}deg)`;

    // For dialog role: default aria-labelledby → trigger for unique accessible names.
    // Use the reference element's actual id (which may differ from triggerId if an external
    // id was passed to Popover.Button — e.g. ToolbarOverflowMenu passes its own menuTriggerId).
    // Fall back to triggerId if the reference element is not yet mounted.
    // For non-dialog roles: omitted — the inner component (e.g. Listbox) owns its own label.
    const referenceId =
      (floatingContext.elements.reference as HTMLElement | null)?.id ??
      triggerId;
    const defaultLabelProps =
      role === "dialog" &&
      !("aria-label" in rest) &&
      !("aria-labelledby" in rest)
        ? { "aria-labelledby": referenceId }
        : {};

    // Remove hover content from the tab order — hover popovers contain read-only text.
    const tabIndexOverride = openOnHover ? { tabIndex: -1 } : {};

    // For non-dialog roles, strip the role attribute from getFloatingProps — useRole sets
    // role="listbox" (etc.) on the content div, but the inner component owns that role.
    // The content div is a neutral positioning wrapper in the combobox pattern.
    const rawFloatingProps = getFloatingProps({
      ...defaultLabelProps,
      ...tabIndexOverride,
      ...rest,
    }) as HTMLAttributes<HTMLDivElement>;
    const { role: _suppressedRole, ...floatingPropsWithoutRole } =
      rawFloatingProps as Record<string, unknown>;
    const floatingProps =
      role === "dialog"
        ? rawFloatingProps
        : (floatingPropsWithoutRole as HTMLAttributes<HTMLDivElement>);

    const contentDiv = (
      <div
        popover="manual"
        data-anv="popover-content"
        className={cx(className, styles["content"], {
          [styles["no-padding"]]: noPadding,
        })}
        ref={mergedFloatingRef}
        style={{ ...floatingStyles, ...style }}
        {...floatingProps}
      >
        <div
          className={styles.scroller}
          role="presentation"
          tabIndex={-1}
          ref={mergedScrollerRef}
          onScroll={onScroll}
          onScrollCapture={onScrollCapture}
        >
          {hideWhileClosed ? (openState ? children : null) : children}
        </div>
        {enableCaret && (
          <span className={styles["arrow"]} ref={arrowRef} style={arrowStyle}>
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14">
              <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z" />
            </svg>
          </span>
        )}
      </div>
    );

    if (role !== "dialog") {
      return contentDiv;
    }

    return (
      <FloatingFocusManager
        context={floatingContext}
        modal={modal ?? false}
        disabled={!openState || openOnHover}
      >
        {contentDiv}
      </FloatingFocusManager>
    );
  },
);

PopoverContent.displayName = "PopoverContent";
